import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { ARENA_COPY, arenaErrorText } from "@/lib/arena-copy";
import { arenaDaysValue, isJoinWindowOpen } from "@/lib/arena-logic";
import { ARENA_JOIN_FROM } from "@/lib/arena-config";
import {
  arenaMeKey,
  arenaRpc,
  clearArenaSrc,
  readArenaSrc,
  storeArenaSrc,
  useArenaCount,
  useArenaMe,
} from "@/lib/arena";
import { setRedirectIntent } from "@/lib/redirect-intent";

const TITLE = "Свои на ARENA — TurkeyDrive";
const DESC = "Найди тех, с кем стоит познакомиться прямо на фестивале ARENA 24–25 октября.";

export const Route = createFileRoute("/arena")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ArenaPage,
});

type DaysChoice = "24" | "25" | "both";

function ArenaPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: count } = useArenaCount();
  const { data: me, isLoading: meLoading } = useArenaMe();
  const [open, setOpen] = useState(true);
  const [soon, setSoon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState<DaysChoice | null>(null);

  useEffect(() => {
    const now = new Date();
    setOpen(isJoinWindowOpen(now));
    setSoon(now.getTime() < ARENA_JOIN_FROM.getTime());
    try {
      const src = new URL(window.location.href).searchParams.get("src");
      storeArenaSrc(src);
    } catch {
      /* ignore */
    }
  }, []);

  const isParticipant = !!me;
  const needsDays = isParticipant && (me?.days ?? []).length === 0;

  // Участник с выбранными днями — сразу в раздел.
  useEffect(() => {
    if (user && isParticipant && !needsDays) navigate({ to: "/arena/people", replace: true });
  }, [user, isParticipant, needsDays, navigate]);

  /** Один экран: согласие + дни. arena_join (если ещё не участник) → arena_set_days; оба повторяемы. */
  async function joinWithDays() {
    if (!days) return;
    setBusy(true);
    if (!isParticipant) {
      const { error } = await arenaRpc.join(readArenaSrc());
      if (error) {
        setBusy(false);
        return toast.error(arenaErrorText(error));
      }
      clearArenaSrc();
      qc.invalidateQueries({ queryKey: ["arena-count"] });
    }
    const { error } = await arenaRpc.setDays(arenaDaysValue(days));
    setBusy(false);
    // Согласие уже записано — при повторном нажатии arena_join просто ничего не сделает.
    if (error) return toast.error(arenaErrorText(error));
    // Строку участника не обновляем здесь: иначе сработает переход сразу в раздел мимо мастера.
    // Мини-мастер покажет только недостающие шаги, а его финал приведёт в /arena/people.
    setRedirectIntent("/arena");
    navigate({ href: "/profile/setup?mode=arena" });
    qc.invalidateQueries({ queryKey: arenaMeKey(user?.id) });
  }

  const loading = authLoading || (!!user && meLoading);

  return (
    <div className="min-h-[100dvh] w-full overflow-x-clip bg-white">
      <main className="mx-auto flex w-full max-w-md flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-[calc(env(safe-area-inset-top)+24px)]">
        <Link to="/" className="self-start text-sm font-semibold text-td-teal">
          TurkeyDrive
        </Link>
        <div className="mt-6 flex flex-col items-center text-center">
          <MrDriveAvatar variant="people" size={96} />
          <h1 className="mt-3 font-display text-3xl font-bold text-td-dark">{ARENA_COPY.title}</h1>
          <p className="mt-1 text-sm font-medium text-td-orange">TAKE · 24–25 октября 2026</p>
          {typeof count === "number" && (
            <p className="mt-3 rounded-full bg-td-teal/10 px-4 py-1.5 text-sm font-semibold text-td-teal">
              {ARENA_COPY.counter(count)}
            </p>
          )}
        </div>

        {loading ? (
          <p className="mt-8 text-center text-sm text-td-muted">Загружаем…</p>
        ) : user && isParticipant && needsDays ? (
          <section className="mt-8 rounded-2xl border border-td-teal/25 bg-td-teal/5 p-5">
            <h2 className="font-display text-2xl font-bold text-td-dark">
              {ARENA_COPY.consentTitle}
            </h2>
            <p className="mt-2 text-sm text-td-dark">{ARENA_COPY.consentText}</p>
            <p className="mt-4 text-sm font-semibold text-td-dark">{ARENA_COPY.daysPick}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  ["24", ARENA_COPY.days24],
                  ["25", ARENA_COPY.days25],
                  ["both", ARENA_COPY.daysBoth],
                ] as [DaysChoice, string][]
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDays(v)}
                  aria-pressed={days === v}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-medium text-td-dark ${days === v ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"}`}
                >
                  {days === v && <Check className="size-4 text-td-teal" />}
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!days || busy}
              onClick={joinWithDays}
              className="cta-solid mt-4 h-14 w-full rounded-full text-lg font-semibold disabled:opacity-50"
            >
              {ARENA_COPY.consentCta}
            </button>
            <Link to="/home" className="mt-3 block text-center text-sm text-td-muted">
              {ARENA_COPY.consentLater}
            </Link>
          </section>
        ) : user && isParticipant ? (
          <p className="mt-8 text-center text-sm text-td-muted">Открываю раздел…</p>
        ) : !open ? (
          <section className="mt-8 rounded-2xl bg-td-soft/60 p-5 text-center">
            <h2 className="text-lg font-semibold text-td-dark">
              {soon ? ARENA_COPY.joinSoon : ARENA_COPY.joinClosed}
            </h2>
            <p className="mt-1 text-sm text-td-muted">
              {soon ? ARENA_COPY.joinSoonNote : ARENA_COPY.joinClosedNote}
            </p>
          </section>
        ) : user ? (
          <section className="mt-8 rounded-2xl border border-td-teal/25 bg-td-teal/5 p-5">
            <h2 className="font-display text-2xl font-bold text-td-dark">
              {ARENA_COPY.consentTitle}
            </h2>
            <p className="mt-2 text-sm text-td-dark">{ARENA_COPY.consentText}</p>
            <p className="mt-4 text-sm font-semibold text-td-dark">{ARENA_COPY.daysPick}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  ["24", ARENA_COPY.days24],
                  ["25", ARENA_COPY.days25],
                  ["both", ARENA_COPY.daysBoth],
                ] as [DaysChoice, string][]
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDays(v)}
                  aria-pressed={days === v}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-medium text-td-dark ${days === v ? "border-td-teal bg-td-teal/10" : "border-td-line bg-white"}`}
                >
                  {days === v && <Check className="size-4 text-td-teal" />}
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!days || busy}
              onClick={joinWithDays}
              className="cta-solid mt-4 h-14 w-full rounded-full text-lg font-semibold disabled:opacity-50"
            >
              {ARENA_COPY.consentCta}
            </button>
            <Link to="/home" className="mt-3 block text-center text-sm text-td-muted">
              {ARENA_COPY.consentLater}
            </Link>
          </section>
        ) : (
          <section className="mt-6 space-y-3 text-center">
            {ARENA_COPY.intro.map((t) => (
              <p key={t} className="text-base text-td-dark">
                {t}
              </p>
            ))}
            <button
              type="button"
              onClick={() => {
                setRedirectIntent("/arena");
                navigate({ to: "/auth" });
              }}
              className="cta-solid mt-4 h-14 w-full rounded-full text-lg font-semibold"
            >
              {ARENA_COPY.ctaSignIn}
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
